import { cn } from "@/utils";

export interface MiniCardHead {
  icon: React.ReactNode;
  label: string;
}

export interface MiniCardProps {
  head: MiniCardHead;
  title: string;
  foot?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

/**
 * MiniCard — inline link card for drawer body / sidebar.
 * Spec: padding 12px, radius-xl, border, bg-card.
 * Hover: border primary, bg primary 4% tint, translateY(-1px), 120ms ease-standard.
 */
export function MiniCard({ head, title, foot, onClick, className }: MiniCardProps) {
  const isInteractive = Boolean(onClick);

  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isInteractive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        "p-3 rounded-[var(--radius-xl)] border border-[var(--border)] bg-[var(--card)]",
        "transition-[border-color,background-color,transform] duration-[120ms]",
        "[transition-timing-function:cubic-bezier(0.2,0,0,1)]",
        isInteractive && [
          "cursor-pointer",
          "hover:border-[var(--primary)]",
          "hover:bg-[color-mix(in_oklch,var(--primary)_4%,var(--card))]",
          "hover:-translate-y-px",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]",
        ],
        className
      )}
    >
      {/* Head: icon + label */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-[var(--muted-foreground)] flex items-center">{head.icon}</span>
        <span className="text-[11px] font-medium text-[var(--muted-foreground)] leading-none">
          {head.label}
        </span>
      </div>

      {/* Title */}
      <p className="text-[13px] font-semibold text-[var(--foreground)] truncate leading-snug">
        {title}
      </p>

      {/* Foot */}
      {foot && (
        <div className="mt-1.5 text-[11px] text-[var(--muted-foreground)] leading-none">
          {foot}
        </div>
      )}
    </div>
  );
}
